// ✅ Updated: index.ts — Decode roles at login and store in Redux

// import { BrowserAuthError, PublicClientApplication } from '@azure/msal-browser';
import {
    AuthenticationResult,
    EventType,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from '@azure/msal-browser';

import { MsalProvider } from '@azure/msal-react';
import ReactDOM from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import App from './App';
import { Constants } from './Constants';
import './index.css';
import { AuthConfig, AuthHelper } from './libs/auth/AuthHelper';
import { store } from './redux/app/store';

import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { BackendServiceUrl } from './libs/services/BaseService';
import { setAuthConfig, setUserRoles } from './redux/features/app/appSlice';
import { appInsightsService } from './libs/services/AppInsightsService';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';

interface DecodedAccessToken {
    roles?: string[];
}

if (!localStorage.getItem('debug')) {
    localStorage.setItem('debug', `${Constants.debug.root}:*`);
}

let container: HTMLElement | null = null;
let root: ReactDOM.Root | undefined = undefined;
let msalInstance: PublicClientApplication;

document.addEventListener('DOMContentLoaded', () => {
    if (!container) {
        container = document.getElementById('root');
        if (!container) {
            throw new Error('Could not find root element');
        }
        root = ReactDOM.createRoot(container);

        void renderApp();
    }
});

export async function renderApp() {
    try {
        const response = await fetch(new URL('authConfig', BackendServiceUrl));
        if (!response.ok) throw new Error();

        const authConfig = (await response.json()) as AuthConfig;
        store.dispatch(setAuthConfig(authConfig));

        // Initialize Application Insights before rendering the app
        const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;
        const reactPlugin = appInsightsService.initialize(connectionString);

        if (AuthHelper.isAuthAAD()) {
            msalInstance = new PublicClientApplication(AuthHelper.getMsalConfig(authConfig));
            await msalInstance.initialize();

            // ✅ MSAL event handling (no invalid imports, no `any`)
            msalInstance.addEventCallback((event) => {
                if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                    const payload = event.payload as AuthenticationResult;
                    msalInstance.setActiveAccount(payload.account);
                }

                // 🔐 Decode roles whenever a token is successfully acquired
                if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
                    const payload = event.payload as AuthenticationResult;
                    if (payload.accessToken) {
                        const decoded = jwtDecode<DecodedAccessToken>(payload.accessToken);
                        store.dispatch(setUserRoles(decoded.roles ?? []));
                    }
                }
            });

            const redirectResponse = await msalInstance.handleRedirectPromise();

            if (redirectResponse?.account) {
                msalInstance.setActiveAccount(redirectResponse.account);
            } else {
                // ✅ Handle refresh scenario with cached accounts
                const accounts = msalInstance.getAllAccounts();
                if (!msalInstance.getActiveAccount() && accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                }
            }

            // 🔐 Get token and decode roles
            const account = msalInstance.getActiveAccount();
            if (account) {
                try {
                    const tokenResponse = await msalInstance.acquireTokenSilent({
                        scopes: [authConfig.aadApiScope],
                        account,
                    });

                    const decoded = jwtDecode<DecodedAccessToken>(tokenResponse.accessToken);
                    store.dispatch(setUserRoles(decoded.roles ?? []));
                } catch (error) {
                    console.error('Error acquiring token silently:', error);

                    // ✅ Proper handling for AADSTS160021 / interaction_required
                    if (error instanceof InteractionRequiredAuthError) {
                        await msalInstance.acquireTokenRedirect({
                            scopes: [authConfig.aadApiScope],
                            account,
                        });
                        return;
                    }

                    // Fallback: force interactive login
                    await msalInstance.loginRedirect({
                        scopes: [authConfig.aadApiScope],
                    });
                    return;
                }
            } else {
                // ✅ No user session yet
                await msalInstance.loginRedirect({
                    scopes: [authConfig.aadApiScope],
                });
                return;
            }

            if (root) {
                root.render(
                    <React.StrictMode>
                        <ReduxProvider store={store}>
                            <MsalProvider instance={msalInstance}>
                                <AppInsightsContext.Provider value={reactPlugin}>
                                    <App />
                                </AppInsightsContext.Provider>
                            </MsalProvider>
                        </ReduxProvider>
                    </React.StrictMode>,
                );
            } else {
                throw new Error('Root element is not initialized');
            }
            return;
        }
    } catch {
        store.dispatch(setAuthConfig(undefined));
    }
}
